import PageHeader from 'App/components/PageHeader'
import { RouteComponentProps } from 'react-router'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import Loading from 'components/Loading'
import { AddOutlined, Gavel } from '@material-ui/icons'
import useOrganizationsCollection from '../hooks/useOrganizationsCollection.new'
import useOrganizationColumns from '../hooks/useOrganizationColumns'
import {
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { Organization } from 'organization/types'
import { Column } from 'react-table'
import { ExtendedPagedTable } from 'core/table/components'
import { useConditionalSelectColumn, useGetColumnState } from 'core/table/table-hooks'
import { NUMBER_OPERATOR_OPTIONS, STRING_OPERATOR_OPTIONS } from 'core/table/interfaces/Filters'
import { useFiltersFromQuery } from 'core/table/components/NewFiltersModal'
import { useStyles } from 'champion/hooks/useChampionColumns'
import DEFAULT_FILTERS, { useGetGlobalFilter } from 'core/table/filters'
import { TableMenuButton } from 'civic-champs-shared'
import { useHasRole } from 'auth/hooks'
import { INTERNAL_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import useManageOrganization from '../hooks/useManageOrganization'
import ContainedButton from 'civic-champs-shared/core/ContainedButton'
import ExportOrganizationsButton from 'organization/component/ExportOrganizationsButton'
import { useShowOrganizationAdd } from '../hooks/useShowOrganizationAddPrompt'
const tableName = 'organizations'

const getOperatorOptions = (column: string) => {
  switch (column) {
    case 'name':
    case 'website':
    case 'createdAt':
    case 'country':
      return STRING_OPERATOR_OPTIONS
    case 'smsCredits':
    case 'emailCredits':
    case 'totalMembers':
    case 'totalVolunteers':
    case 'totalHoursVolunteered':
      return NUMBER_OPERATOR_OPTIONS
    default:
      return []
  }
}

export const Organizations = (props: RouteComponentProps) => {
  return (
    <PageHeader title="Organizations" subTitle="Manage your organizations' information">
      <OrganizationsComponent {...props} />
    </PageHeader>
  )
}

const OrganizationsComponent = (props: RouteComponentProps) => {
  const { location, history } = props
  const isInternalAdmin = useHasRole(INTERNAL_ADMIN)
  const skipReset = useRef<boolean>()
  const columns: Column<Organization>[] = useOrganizationColumns()
  const columnsStyles = useStyles()
  const { filters } = useFiltersFromQuery(location.search, getOperatorOptions)
  const [{ organizations, loading, loadingProgress, initiallyLoaded }, { eagerAdd }, eventListeners] =
    useOrganizationsCollection()
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  const addOrganization = useShowOrganizationAdd(eagerAdd)

  const globalFilter = useGetGlobalFilter({
    columns,
    filterTypes: { ...DEFAULT_FILTERS },
    getOperatorOptions,
  })

  const table = useTable(
    {
      initialState: {
        hiddenColumns: ['website', 'smsCredits', 'emailCredits'],
        //@ts-ignore
        globalFilter: '',
        filters,
      },
      columns,
      data: organizations,
      autoResetSortBy: !skipReset.current,
      autoResetPage: !skipReset.current,
      globalFilter,
      filterTypes: { ...DEFAULT_FILTERS },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnsStyles.selectColumn),
  )

  const [selectedRows] = useMemo(() => {
    return [table.selectedFlatRows, table.selectedFlatRows.length]
  }, [table.selectedFlatRows])

  useEffect(() => {
    fetchColumnState(tableName)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const manageOrganization = useManageOrganization()
  const handleStartViewing = useCallback(() => {
    if (!selectedRows[0]) return
    manageOrganization(selectedRows[0].original)
  }, [manageOrganization, selectedRows])

  if (!initiallyLoaded || loading || columnStateLoading) {
    return <Loading progress={loadingProgress} animateProgress />
  }

  const handleAddClick = () => {
    addOrganization()
  }

  return (
    <div>
      <ExtendedPagedTable<Organization>
        columns={columns}
        table={table}
        history={history}
        location={location}
        getOperatorOptions={getOperatorOptions}
        columnState={columnState}
        tableName={tableName}
        searchPlaceholder="Search Organizations"
        filterSubHeader="Select organization attributes to filter by:"
        useGlobalSearch
        useFilters
        skipReset={skipReset}
        eventListeners={eventListeners}
        addButton={
          <ContainedButton
            startIcon={<AddOutlined />}
            onClick={() => {
              handleAddClick()
            }}
          >
            Add Organization
          </ContainedButton>
        }
        buttons={
          <>
            <TableMenuButton
              startIcon={<Gavel />}
              onClick={handleStartViewing}
              disabled={!isInternalAdmin || selectedRows.length !== 1}
              rightMargin
            >
              Manage Organization
            </TableMenuButton>
            <ExportOrganizationsButton data={selectedRows} disabled={selectedRows.length === 0} />
          </>
        }
      />
    </div>
  )
}
